import type { ProductSortKey, ProductUnitListQuery } from '@erpgold/shared';
export type { ProductUnitListQuery } from '@erpgold/shared';

// ADR-013: API JSON snake_case end-to-end. Pagination key chuẩn = page_size
// (giống OrderListQuery). FE state useTableState giữ camelCase (TS variable),
// transform tại site gọi hook list (vd: { page_size: pageSize }).
export interface ProductListQuery {
  q?: string;
  // EG-1731: filter multi-select — mỗi field nhận 1+ giá trị (OR trong cùng field).
  category_id?: string[];
  brand_id?: string[];
  state?: number[];
  sale_type?: string[];
  page?: number;
  page_size?: number;
  sort?: ProductSortKey;
  attrs?: Record<string, string[]>;  // multi-value per attribute
}

export interface FilterableAttributeValue {
  label: string;
  value: string;
}

export interface FilterableAttribute {
  code: string;
  name: string;
  values: FilterableAttributeValue[];
}

/** EG-78: snake_case property per ADR-013 D4. group → BE resolve thành array enum. */
export interface OrderListQuery {
  page?: number;
  page_size?: number;
  channel_group?: string;
  status_group?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
}

export interface InventoryListQuery {
  page?: number;
  page_size?: number;
  category?: string;
  karat?: string;
  store_id?: string;
  warehouse_id?: string;
  search?: string;
}

export interface StaffListQuery {
  page?: number;
  page_size?: number;
  /** Filter by single role_id — matches if role_ids array contains this id. */
  role_id?: string;
  status?: string;
  search?: string;
  store_id?: string;
}

export const productKeys = {
  all: ['products'] as const,
  lists: () => [...productKeys.all, 'list'] as const,
  list: (q: ProductListQuery) => [...productKeys.lists(), q] as const,
  details: () => [...productKeys.all, 'detail'] as const,
  detail: (id: string) => [...productKeys.details(), id] as const,
  variants: (productId: string) => [...productKeys.all, 'variants', productId] as const,
  variantDetail: (productId: string, variantId: string) =>
    [...productKeys.all, 'variants', productId, variantId] as const,
};

export const orderKeys = {
  all: ['orders'] as const,
  lists: () => [...orderKeys.all, 'list'] as const,
  list: (q: OrderListQuery) => [...orderKeys.lists(), q] as const,
  details: () => [...orderKeys.all, 'detail'] as const,
  detail: (id: string) => [...orderKeys.details(), id] as const,
};

export const inventoryKeys = {
  all: ['inventory'] as const,
  lists: () => [...inventoryKeys.all, 'list'] as const,
  list: (q: InventoryListQuery) => [...inventoryKeys.lists(), q] as const,
  history: (productId: string) => [...inventoryKeys.all, 'history', productId] as const,
  movements: (q: Record<string, unknown>) =>
    [...inventoryKeys.all, 'movements', q] as const,
};

export const staffKeys = {
  all: ['staffs'] as const,
  lists: () => [...staffKeys.all, 'list'] as const,
  list: (q: StaffListQuery) => [...staffKeys.lists(), q] as const,
  detail: (id: string) => [...staffKeys.all, 'detail', id] as const,
};

export const goldPriceKeys = {
  all: ['goldPrice'] as const,
  current: () => [...goldPriceKeys.all, 'current'] as const,
};

export const reportKeys = {
  all: ['reports'] as const,
  revenue: (range: { from: string; to: string }) => [...reportKeys.all, 'revenue', range] as const,
  eod: (date: string, storeId?: string) =>
    [...reportKeys.all, 'eod', date, storeId ?? null] as const,
  eodHistory: (query: { page: number; pageSize: number; storeId?: string }) =>
    [...reportKeys.all, 'eod-history', query] as const,
  profit: (query: { startDate: string; endDate: string; storeId?: string; groupBy?: string }) =>
    [...reportKeys.all, 'profit', query] as const,
  products: (query: {
    startDate: string;
    endDate: string;
    storeId?: string;
    sortBy?: string;
    limit?: number;
  }) => [...reportKeys.all, 'products', query] as const,
  staff: (query: { startDate: string; endDate: string; storeId?: string; sortBy?: string }) =>
    [...reportKeys.all, 'staff', query] as const,
  customersRfm: (query: { startDate: string; endDate: string; storeId?: string }) =>
    [...reportKeys.all, 'customers-rfm', query] as const,
  inventoryValuation: (query: {
    as_of_date?: string;
    store_id?: string;
    category?: string;
  }) => [...reportKeys.all, 'inventory-valuation', query] as const,
  builderSavedList: () => [...reportKeys.all, 'builder', 'saved-list'] as const,
};

export const planKeys = {
  all: ['plan'] as const,
  current: () => [...planKeys.all, 'current'] as const,
  payments: () => [...planKeys.all, 'payments'] as const,
  list: () => [...planKeys.all, 'list'] as const,
  paymentMethods: () => [...planKeys.all, 'payment-methods'] as const,
  paymentStatus: (paymentId: string) => [...planKeys.all, 'payment-status', paymentId] as const,
  subscription: () => [...planKeys.all, 'subscription'] as const,
  changePlanPreview: (slug: string) => [...planKeys.all, 'change-plan-preview', slug] as const,
  billingPayments: (page: number, limit: number) =>
    [...planKeys.all, 'billing-payments', page, limit] as const,
};

export const storeKeys = {
  all: ['store'] as const,
  settings: () => [...storeKeys.all, 'settings'] as const,
  theme: () => [...storeKeys.all, 'theme'] as const,
  stores: () => [...storeKeys.all, 'stores'] as const,
  store: (id: string) => [...storeKeys.all, 'store', id] as const,
};

export const hardwareKeys = {
  all: ['hardware'] as const,
  status: () => [...hardwareKeys.all, 'status'] as const,
  devices: () => [...hardwareKeys.all, 'devices'] as const,
};

export const onboardingKeys = {
  all: ['onboarding'] as const,
  progress: () => [...onboardingKeys.all, 'progress'] as const,
};

export const roleKeys = {
  all: ['roles'] as const,
  lists: () => [...roleKeys.all, 'list'] as const,
  detail: (id: string) => [...roleKeys.all, 'detail', id] as const,
};

export interface CustomerListQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  rank?: string;
  tag?: string;
}

export const customerKeys = {
  all: ['customers'] as const,
  lists: () => [...customerKeys.all, 'list'] as const,
  list: (q: CustomerListQuery) => [...customerKeys.lists(), q] as const,
  detail: (id: string) => [...customerKeys.all, 'detail', id] as const,
  purchases: (id: string) => [...customerKeys.all, 'purchases', id] as const,
  loyaltySettings: () => [...customerKeys.all, 'loyalty-settings'] as const,
  addresses: (id: string) => [...customerKeys.all, 'addresses', id] as const,
  banks: (id: string) => [...customerKeys.all, 'banks', id] as const,
};

export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  unreadCount: () => [...notificationKeys.all, 'unreadCount'] as const,
};

export const shiftKeys = {
  all: ['shifts'] as const,
  lists: () => [...shiftKeys.all, 'list'] as const,
  current: () => [...shiftKeys.all, 'current'] as const,
  detail: (id: string) => [...shiftKeys.all, 'detail', id] as const,
  daily: (date: string, storeId?: string) =>
    [...shiftKeys.all, 'daily', date, storeId ?? 'all'] as const,
};

export const dashboardKeys = {
  all: ['dashboard'] as const,
  summary: (role?: string) => [...dashboardKeys.all, 'summary', role ?? 'any'] as const,
};

export interface ShippingListQuery {
  page?: number;
  limit?: number;
  state?: string;
  q?: string;
  store_id?: string;
}

export interface GhtkLogsQuery {
  page?: number;
  limit?: number;
  order_number?: string;
}

/** EG-332/345 — Settings (Owner-only tenant config) */
export const settingsKeys = {
  all: ['settings'] as const,
  full: () => [...settingsKeys.all, 'full'] as const,
  business: () => [...settingsKeys.all, 'business'] as const,
  payment: () => [...settingsKeys.all, 'payment'] as const,
  einvoice: () => [...settingsKeys.all, 'einvoice'] as const,
  notifications: () => [...settingsKeys.all, 'notifications'] as const,
  integrations: () => [...settingsKeys.all, 'integrations'] as const,
  bankAccounts: () => [...settingsKeys.all, 'bank-accounts'] as const,
  eWallets: () => [...settingsKeys.all, 'e-wallets'] as const,
  cardTerminals: () => [...settingsKeys.all, 'card-terminals'] as const,
  apiKeys: () => [...settingsKeys.all, 'api-keys'] as const,
  auditLog: (q?: Record<string, unknown>) =>
    [...settingsKeys.all, 'audit-log', q ?? {}] as const,
};

/** EG-504..EG-529 — Payroll module */
export const payrollKeys = {
  all: ['payroll'] as const,
  config: () => [...payrollKeys.all, 'config'] as const,
  components: () => [...payrollKeys.all, 'components'] as const,
  profile: (userId: string) => [...payrollKeys.all, 'profile', userId] as const,
  runs: () => [...payrollKeys.all, 'runs'] as const,
  runDetail: (id: string) => [...payrollKeys.runs(), 'detail', id] as const,
  dashboard: () => [...payrollKeys.all, 'dashboard'] as const,
  reports: () => [...payrollKeys.all, 'reports'] as const,
};

export const shippingKeys = {
  all: ['shipping'] as const,
  lists: () => [...shippingKeys.all, 'list'] as const,
  list: (q: ShippingListQuery) => [...shippingKeys.lists(), q] as const,
  detail: (id: string) => [...shippingKeys.all, 'detail', id] as const,
  tracking: (id: string) => [...shippingKeys.all, 'tracking', id] as const,
  pickupLocations: () => [...shippingKeys.all, 'pickup-locations'] as const,
  ghtkLogs: (q: GhtkLogsQuery) => [...shippingKeys.all, 'ghtk-logs', q] as const,
};

// === Products module — master data (Phase 1) ===

export interface CategoryListQuery {
  parent_id?: string;
}

export const categoryKeys = {
  all: ['categories'] as const,
  lists: () => [...categoryKeys.all, 'list'] as const,
  list: (q: CategoryListQuery) => [...categoryKeys.lists(), q] as const,
  tree: () => [...categoryKeys.all, 'tree'] as const,
  detail: (id: string) => [...categoryKeys.all, 'detail', id] as const,
};

export interface BrandListQuery {
  q?: string;
  page?: number;
  page_size?: number;
}

export const brandKeys = {
  all: ['brands'] as const,
  lists: () => [...brandKeys.all, 'list'] as const,
  list: (q: BrandListQuery) => [...brandKeys.lists(), q] as const,
  detail: (id: string) => [...brandKeys.all, 'detail', id] as const,
};

export const productUnitKeys = {
  all: ['product-units'] as const,
  lists: () => [...productUnitKeys.all, 'list'] as const,
  list: (q: ProductUnitListQuery) => [...productUnitKeys.lists(), q] as const,
  detail: (id: string) => [...productUnitKeys.all, 'detail', id] as const,
};

export interface AttributeListQuery {
  q?: string;
  page?: number;
  limit?: number;
  sort?: 'code' | 'name' | 'created_at';
  order?: 'asc' | 'desc';
}

export const attributeKeys = {
  all: ['attributes'] as const,
  lists: () => [...attributeKeys.all, 'list'] as const,
  list: (q: AttributeListQuery) => [...attributeKeys.lists(), q] as const,
  detail: (code: string) => [...attributeKeys.all, 'detail', code] as const,
};

export const attributeValueKeys = {
  all: ['attribute-values'] as const,
  byAttribute: (code: string) => [...attributeValueKeys.all, 'by-attribute', code] as const,
  detail: (code: string, valueId: string) =>
    [...attributeValueKeys.all, 'detail', code, valueId] as const,
};

// === Accounting module (S4.1) ===

export interface EInvoiceListQuery {
  page?: number;
  limit?: number;
  status?: string;
  from_date?: string;
  to_date?: string;
  search?: string;
}

export interface GovInvoiceListQuery {
  page?: number;
  limit?: number;
  status?: string;
  from_date?: string;
  to_date?: string;
}

export interface SalesReturnListQuery {
  page?: number;
  limit?: number;
  from_date?: string;
  to_date?: string;
  status?: string;
}

export interface PurchaseInvoiceListQuery {
  page?: number;
  limit?: number;
  from_date?: string;
  to_date?: string;
  status?: string;
  supplier_id?: string;
}

export interface PrepaidExpenseListQuery {
  page?: number;
  limit?: number;
  status?: string;
}

export const accountingKeys = {
  all: ['accounting'] as const,

  // E-Invoices
  eInvoices: () => [...accountingKeys.all, 'e-invoices'] as const,
  eInvoiceList: (q?: EInvoiceListQuery) => [...accountingKeys.eInvoices(), 'list', q] as const,
  eInvoiceDetail: (id: string) => [...accountingKeys.eInvoices(), 'detail', id] as const,
  eInvoiceStats: () => [...accountingKeys.eInvoices(), 'stats'] as const,

  // Gov Invoices (TCT)
  govInvoices: () => [...accountingKeys.all, 'gov-invoices'] as const,
  govInvoiceList: (q?: GovInvoiceListQuery) => [...accountingKeys.govInvoices(), 'list', q] as const,

  // Sales Returns
  salesReturns: () => [...accountingKeys.all, 'sales-returns'] as const,
  salesReturnList: (q?: SalesReturnListQuery) => [...accountingKeys.salesReturns(), 'list', q] as const,

  // Purchase Invoices
  purchaseInvoices: () => [...accountingKeys.all, 'purchase-invoices'] as const,
  purchaseInvoiceList: (q?: PurchaseInvoiceListQuery) => [...accountingKeys.purchaseInvoices(), 'list', q] as const,
  purchaseInvoiceDetail: (id: string) => [...accountingKeys.purchaseInvoices(), 'detail', id] as const,

  // Prepaid Expenses
  prepaidExpenses: () => [...accountingKeys.all, 'prepaid-expenses'] as const,
  prepaidExpenseList: (q?: PrepaidExpenseListQuery) => [...accountingKeys.prepaidExpenses(), 'list', q] as const,
  prepaidExpenseDetail: (id: string) => [...accountingKeys.prepaidExpenses(), 'detail', id] as const,
  prepaidExpenseSummary: (id: string) => [...accountingKeys.prepaidExpenses(), 'summary', id] as const,
  monthlyAllocations: (year?: number, month?: number) => [...accountingKeys.prepaidExpenses(), 'monthly', year, month] as const,
};

export const warehouseKeys = {
  all: ['warehouses'] as const,
  lists: () => [...warehouseKeys.all, 'list'] as const,
  detail: (id: string) => [...warehouseKeys.all, 'detail', id] as const,
};

// === Fixed Assets (TSCĐ) ===

export interface FixedAssetGroupListQuery {
  page?: number;
  limit?: number;
  search?: string;
}

export interface FixedAssetListQuery {
  page?: number;
  limit?: number;
  status?: string;
  group_id?: string;
  department_id?: string;
  search?: string;
}

export interface FixedAssetDepreciationListQuery {
  fixed_asset_id?: string;
  year?: number;
  month?: number;
  status?: string;
}

export const fixedAssetKeys = {
  all: ['fixed-assets'] as const,
  groups: () => [...fixedAssetKeys.all, 'groups'] as const,
  groupList: (q?: FixedAssetGroupListQuery) => [...fixedAssetKeys.groups(), 'list', q] as const,
  groupDetail: (id: string) => [...fixedAssetKeys.groups(), 'detail', id] as const,
  assets: () => [...fixedAssetKeys.all, 'assets'] as const,
  assetList: (q?: FixedAssetListQuery) => [...fixedAssetKeys.assets(), 'list', q] as const,
  assetDetail: (id: string) => [...fixedAssetKeys.assets(), 'detail', id] as const,
  depreciation: (assetId: string) => [...fixedAssetKeys.all, 'depreciation', assetId] as const,
  monthlyDepreciation: (year?: number, month?: number) => [...fixedAssetKeys.all, 'monthly-depreciation', year, month] as const,
  pendingDepreciation: () => [...fixedAssetKeys.all, 'pending-depreciation'] as const,
};

// === Tool & Supplies (CCDC) ===

export interface ToolSupplyListQuery {
  page?: number;
  limit?: number;
  status?: string;
  department_id?: string;
  warehouse_id?: string;
  search?: string;
}

export const toolSupplyKeys = {
  all: ['tool-supplies'] as const,
  list: (q?: ToolSupplyListQuery) => [...toolSupplyKeys.all, 'list', q] as const,
  detail: (id: string) => [...toolSupplyKeys.all, 'detail', id] as const,
  summary: (id: string) => [...toolSupplyKeys.all, 'summary', id] as const,
  monthlyAllocations: (year?: number, month?: number) => [...toolSupplyKeys.all, 'monthly-allocations', year, month] as const,
  accountingTrail: (id: string) => [...toolSupplyKeys.all, 'accounting-trail', id] as const,
};

// === Opening Balances (Số dư đầu kỳ) ===

export const openingBalanceKeys = {
  all: ['opening-balances'] as const,
  tree: () => [...openingBalanceKeys.all, 'tree'] as const,
  summary: () => [...openingBalanceKeys.all, 'summary'] as const,
  history: (accountId: string) => [...openingBalanceKeys.all, 'history', accountId] as const,
};
